import { Copy, Edit, FileStack, Lock, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeletePromptTemplate,
  usePromptTemplates,
  type PromptTemplateRecord,
} from '@/hooks/usePromptData';

import type { PromptScope } from '@goenhub/shared';

import { TemplateFormDialog } from './TemplateFormDialog';

type ScopeFilter = 'all' | PromptScope;

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; template: PromptTemplateRecord }
  | { mode: 'duplicate'; template: PromptTemplateRecord };

const SCOPE_LABEL: Record<PromptScope, string> = {
  mail: 'メール',
  line_news: 'LINE NEWS',
  both: '両方',
};

export function PromptTemplatesPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = usePromptTemplates();
  const deleteMutation = useDeletePromptTemplate();
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');

  const filtered = useMemo(() => {
    if (!data) return [];
    if (scopeFilter === 'all') return data;
    return data.filter((t) => t.scope === scopeFilter);
  }, [data, scopeFilter]);

  const handleDelete = async (t: PromptTemplateRecord) => {
    if (t.isDefault) {
      toast.error('デフォルトテンプレートは削除できません');
      return;
    }
    if (!window.confirm(`テンプレート「${t.name}」を削除します。よろしいですか？`)) return;
    try {
      await deleteMutation.mutateAsync(t.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const closeDialog = () => setDialog({ mode: 'closed' });

  return (
    <Screen
      title="テンプレート管理"
      description="メール生成 / LINE NEWS 生成で共通利用するプロンプト雛形を管理します"
      actions={
        canEdit ? (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setDialog({ mode: 'create' })}
          >
            <Plus />
            新規作成
          </Button>
        ) : undefined
      }
    >
      <Tabs value={scopeFilter} onValueChange={(v) => setScopeFilter(v as ScopeFilter)}>
        <TabsList>
          <TabsTrigger value="all">すべて</TabsTrigger>
          <TabsTrigger value="mail">メール</TabsTrigger>
          <TabsTrigger value="line_news">LINE NEWS</TabsTrigger>
          <TabsTrigger value="both">両方</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <Spinner />
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {filtered.map((t) => (
            <div
              key={t.id}
              className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4"
            >
              <div className="flex items-start gap-2 mb-2">
                <FileStack className="size-5 text-[var(--accent)] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-sm truncate">{t.name}</h3>
                    <Badge variant="accent">{t.category}</Badge>
                    <Badge variant="default">{SCOPE_LABEL[t.scope]}</Badge>
                    {t.isDefault && (
                      <Badge variant="default">
                        <Lock className="size-3" />
                        デフォルト
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] line-clamp-2 leading-relaxed">
                    {t.description || '（説明なし）'}
                  </p>
                </div>
              </div>
              <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 mb-3">
                <span>{t.fields?.length ?? 0} フィールド</span>
                <code className="text-[10px] font-mono">{t.id}</code>
              </div>
              {canEdit && (
                <div className="flex justify-end gap-1 pt-3 border-t border-[var(--border)]">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDialog({ mode: 'duplicate', template: t })}
                  >
                    <Copy />
                    複製
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDialog({ mode: 'edit', template: t })}
                  >
                    <Edit />
                    編集
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={t.isDefault}
                    onClick={() => handleDelete(t)}
                    title={t.isDefault ? 'デフォルトは削除不可' : '削除'}
                  >
                    <Trash2 />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={FileStack}
          title="テンプレートが登録されていません"
          description="「新規作成」ボタンから追加できます"
        />
      )}

      <TemplateFormDialog
        open={dialog.mode !== 'closed'}
        onOpenChange={(o) => !o && closeDialog()}
        initial={
          dialog.mode === 'edit' || dialog.mode === 'duplicate' ? dialog.template : undefined
        }
        isDuplicate={dialog.mode === 'duplicate'}
      />
    </Screen>
  );
}
